/* eslint-disable no-unused-expressions */
import 'requestidlecallback-polyfill';
import './table-override.scss';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ModuleRegistry, } from '@ag-grid-community/core';
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model';
import { AgGridReact } from '@ag-grid-community/react';
import { create } from '@thoughtspot/logger';
import cx from 'classnames';
import _ from 'lodash';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { copyTextToClipboard, getResponsiveBreakpoint } from '../../_utils';
import { WrapAsCordComponent } from '../../cord';
import { LoadingIndicator } from '../../loading-indicator/loading-indicator';
import { SafeHTML } from '../../safe-html';
import { calculateMarginBySpaces, DEFAULT_ROW_HEIGHT, encodeURIForCaption, getDataFromSelectionRange, getHighestHeightOfRowChildren, LINK_TARGET, resetHeaderHeight, ROW_CONTAINER_SELECTOR, screenSizeToSummaryHeightMapping, TABLE_CONTENT_DENSITY, TABLE_THEME, THEME_TO_CLASSNAMES } from '../table-utils';
import { OtelOp } from './otel-operations';
import styles from './table.module.scss';
import { startSpan } from './tracer';
const logger = create('plain-table');
ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    InfiniteRowModelModule,
]);
const LoaderComponent = (props) => {
    useEffect(() => {
        props.onLoad(props.context);
    }, [props]);
    return (React.createElement("div", { className: styles.loaderContextualWrapper },
        React.createElement(LoadingIndicator.Contextual, null)));
};
const CAPTION_REGEX = /{caption}(.*){\/caption}(.*)/g;
const isCaptioned = (value) => _.isString(value) && !!value.match(CAPTION_REGEX);
const getCaptionHtml = (value, target = LINK_TARGET.BLANK) => {
    return value.replace(CAPTION_REGEX, (_placeHolder, caption, link) => {
        const captionLabel = caption.replace(new RegExp('<[^>]*>', 'g'), '');
        const encodedURI = encodeURIForCaption(link);
        return `<a href="${encodedURI}" class="${styles.caption}" target="${target}" rel="noopener">${captionLabel}</a>`;
    });
};
const getValueGetter = (column) => {
    if (!column.valueGetter) {
        return;
    }
    // eslint-disable-next-line consistent-return
    return (params) => {
        if (params.data.tsInternalShowloader) {
            return true;
        }
        const valueGetter = column.valueGetter || _.noop;
        return valueGetter(_.get(params.data, column.id));
    };
};
const getCellStyler = (column) => {
    if (!column.cellStyle) {
        return;
    }
    // eslint-disable-next-line consistent-return
    return (params) => {
        var _a;
        const cellStyle = column.cellStyle;
        return cellStyle === null || cellStyle === void 0 ? void 0 : cellStyle(_.get(params.data, column.id), {
            rowPinned: (_a = params.node) === null || _a === void 0 ? void 0 : _a.rowPinned,
            context: params.context,
            rowData: params.data,
        });
    };
};
const ColumnPropsToProcess = [
    'id',
    'headerName',
    'valueFormatter',
    'valueGetter',
    'cellStyle',
    'headerComponent',
    'wrapText',
    'highlight',
];
const getValueFormatter = (column) => (params) => {
    if (column.valueFormatter && !_.isUndefined(params.value)) {
        const value = params.value;
        return column.valueFormatter(value, {
            context: params.context,
            rowData: params.data,
        });
    }
    return params.value;
};
const getCellRenderer = (cellLinkTarget, isHtmlRenderingDisabled, isClipMode) => (params) => {
    const { value } = params;
    if (isHtmlRenderingDisabled) {
        return React.createElement("div", null, params.valueFormatted);
    }
    if (isCaptioned(value)) {
        const htmlString = getCaptionHtml(value, cellLinkTarget);
        return React.createElement(SafeHTML, { html: htmlString, config: { ADD_ATTR: ['target'] } });
    }
    if (isClipMode) {
        const { leading } = calculateMarginBySpaces(params.valueFormatted);
        if (leading) {
            // For now, only adding margin to the left to visually preserve leading spaces.
            // Trailing spaces are considered fine as they are.
            return (React.createElement(SafeHTML, { html: params.valueFormatted, style: { marginLeft: `${leading}px` }, config: { ADD_ATTR: ['target'] }, hyperLinkSanitization: true }));
        }
    }
    return (React.createElement(SafeHTML, { html: params.valueFormatted, config: { ADD_ATTR: ['target'] }, hyperLinkSanitization: true }));
};
const isCurrentCellSelected = (selectedRange, rowIndex, colIndex) => {
    const minRowIndex = Math.min(selectedRange.startRowIndex, selectedRange.endRowIndex);
    const maxRowIndex = Math.max(selectedRange.startRowIndex, selectedRange.endRowIndex);
    const minColumnIndex = Math.min(selectedRange.startColumnIndex, selectedRange.endColumnIndex);
    const maxColumnIndex = Math.max(selectedRange.startColumnIndex, selectedRange.endColumnIndex);
    return (colIndex <= maxColumnIndex &&
        colIndex >= minColumnIndex &&
        rowIndex <= maxRowIndex &&
        rowIndex >= minRowIndex);
};
const getColumnDefs = (columns, { columnHighLightAttr, headerComponent, headerComponentParams, showSummary, summaryComponent, summaryComponentParams, cellLinkTarget, selectedRange, isHtmlRenderingDisabled, }) => _.map(columns, (column, index) => {
    const field = column.id || index.toString();
    const lockPinned = true;
    const valueFormatter = getValueFormatter(column);
    const columnProps = {
        suppressMovable: column.suppressMovable,
        colId: field,
        field,
        lockPinned,
        headerName: _.get(column, 'headerName', column.id || index.toString()),
        valueGetter: getValueGetter(column),
        cellClassRules: {
            [styles.wrap]: () => column.wrapText || false, // Added false because wrapText is optional property and cellClassRules expect bool or string
            [styles.highlight]: () => _.get(columnHighLightAttr, column.id, false),
            [styles.compact]: ({ context, node, }) => {
                if (context.density === TABLE_CONTENT_DENSITY.COMPACT) {
                    // for headline, compact class will add padding and as a result some part of value will hide hence disable this class for summary
                    if (showSummary &&
                        summaryComponent &&
                        _.isEmpty(node.data)) {
                        return false;
                    }
                    return true;
                }
                return false;
            },
            [styles.cellOutline]: ({ rowIndex, node, }) => {
                // If a row is pinned (headline rows), we don't
                // apply the cell outline style on selection
                if (node.rowPinned) {
                    return false;
                }
                const singleCellSelected = selectedRange.startColumnIndex ===
                    selectedRange.endColumnIndex &&
                    selectedRange.startRowIndex ===
                        selectedRange.endRowIndex;
                // Cell outline is not applied for
                // multiple cell selections.
                return (singleCellSelected &&
                    isCurrentCellSelected(selectedRange, rowIndex, index));
            },
            [styles.cellSelected]: ({ rowIndex, node, }) => {
                if (node.rowPinned) {
                    return false;
                }
                return isCurrentCellSelected(selectedRange, rowIndex, index);
            },
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        cellStyle: getCellStyler(column),
        headerComponent,
        headerComponentParams: {
            isHighlighted: _.get(columnHighLightAttr, column.id, false),
            wrapped: column.wrapText,
            ...headerComponentParams,
        },
        cellRenderer: getCellRenderer(cellLinkTarget, isHtmlRenderingDisabled, !column.wrapText),
        ..._.omit(column, ColumnPropsToProcess),
    };
    if (showSummary) {
        columnProps.cellRendererSelector = (params) => {
            if (params.node.rowPinned) {
                return {
                    component: summaryComponent,
                    params: { field, ...summaryComponentParams },
                };
            }
            return undefined;
        };
    }
    if (valueFormatter) {
        columnProps.valueFormatter = valueFormatter;
    }
    return columnProps;
});
export const getUpdatedSelectedRange = (rowIndex, colIndex, selectedRange) => {
    const rowMin = Math.min(selectedRange.startRowIndex, selectedRange.endRowIndex);
    const rowMax = Math.max(selectedRange.startRowIndex, selectedRange.endRowIndex);
    const colMin = Math.min(selectedRange.startColumnIndex, selectedRange.endColumnIndex);
    const colMax = Math.max(selectedRange.startColumnIndex, selectedRange.endColumnIndex);
    let updatedSelectedRange = { ...selectedRange };
    if (rowIndex < rowMin ||
        rowIndex > rowMax ||
        colIndex < colMin ||
        colIndex > colMax) {
        updatedSelectedRange = {
            startRowIndex: rowIndex,
            endRowIndex: rowIndex,
            startColumnIndex: colIndex,
            endColumnIndex: colIndex,
        };
    }
    return updatedSelectedRange;
};
const TableDataView = forwardRef(({ data, location = {}, columns, onColumnOrderUpdate, onColumnResize, getAnnotationLocationConfig, getAnnotationRenderedConfig, annotationClickEventHandler, isPrintMode = false, headerComponent, headerComponentParams, calcRowStyle, showSummary = false, summaryComponent, summaryComponentParams, theme = TABLE_THEME.OUTLINE, 
// themeMode is accepted for backward compatibility but ignored;
// dark mode is driven by the [data-theme='dark'] attribute on
// <html>. See table-override.scss.
onCellContextClicked, onCellDoubleClicked, onCellClicked, density = TABLE_CONTENT_DENSITY.REGULAR, onFirstDataRendered, onRowDataChangedCb, context, onBodyScrollToBottom, hasMoreData, cellLinkTarget, suppressColumnVirtualisation, isDebounceVerticalScrollbar, shouldScale, isContextMenuEnabledOnLeftClick, isContextMenuEnabledOnRightClick, isHtmlRenderingDisabled, onSortingChanged, otelContext, otelEnabled = false, }, ref) => {
    var _a;
    const DEFAULT_COL_DEF = useMemo(() => {
        return {
            resizable: true,
            // min width is set to 4px so that user can completely collapse the column in accordance with [SCAL-101101]
            minWidth: 4,
        };
    }, []);
    const gridApi = useRef(null);
    const gridDOMRef = useRef(null);
    const selectedRange = useRef({});
    const timeoutIds = useRef([]);
    const resetRowHeightIdleRef = useRef(0);
    const redrawIdleRef = useRef(0);
    const renderSpanRef = useRef(null);
    const renderT0Ref = useRef(0);
    const onIdleRunner = (cancelAllIdle = _.noop, onIdleCb = _.noop) => {
        var _a, _b;
        try {
            if (!(gridDOMRef === null || gridDOMRef === void 0 ? void 0 : gridDOMRef.current) || !(gridApi === null || gridApi === void 0 ? void 0 : gridApi.current)) {
                cancelAllIdle();
                return 0;
            }
            // check if gridapi has any frame queues to render
            if (!((_a = gridApi.current) === null || _a === void 0 ? void 0 : _a.isAnimationFrameQueueEmpty())) {
                (_b = gridApi.current) === null || _b === void 0 ? void 0 : _b.flushAllAnimationFrames();
            }
            // no more animation frames in the loop
            return window.requestIdleCallback(() => {
                if (!(gridDOMRef === null || gridDOMRef === void 0 ? void 0 : gridDOMRef.current) || !(gridApi === null || gridApi === void 0 ? void 0 : gridApi.current)) {
                    cancelAllIdle();
                    return 0;
                }
                return onIdleCb();
            }, { timeout: 250 });
        }
        catch (e) {
            cancelAllIdle();
            logger.warn(e);
        }
        return 0;
    };
    const cordAnnotationCaptureHandler = (_position, element) => {
        if (_.isNil(getAnnotationLocationConfig)) {
            return null;
        }
        return getAnnotationLocationConfig(element, gridApi.current);
    };
    const cordAnnotationRenderer = (annotation) => {
        if (_.isNil(getAnnotationRenderedConfig)) {
            return null;
        }
        return getAnnotationRenderedConfig(gridDOMRef.current, annotation.location, gridApi.current);
    };
    const cordAnnotationClickHandler = (annotation) => {
        if (_.isUndefined(annotationClickEventHandler)) {
            return;
        }
        annotationClickEventHandler(gridDOMRef.current, annotation.location, gridApi.current);
    };
    /*
     * forceRerenderForSelectedRange is required to reflect the highlight selection,
     * beacuse now we are setting the range using ref not setState.
     * Not using setState because while doing the setState in eventHandler blocking
     * the event to propagate.
     */
    const [, forceRerenderForSelectedRange] = useState({});
    const [columnHighLightAttr, updateColumnHighlight] = useState(_.transform(columns, (colHighlight, col) => _.set(colHighlight, col.id, col.highlight || false), {}));
    const cancelAllResetRowHeightIdle = () => {
        if (resetRowHeightIdleRef.current !== 0) {
            window.cancelIdleCallback(resetRowHeightIdleRef.current);
            resetRowHeightIdleRef.current = 0;
        }
    };
    /*
     * resetRowHeights will re-calculate the row height by calling getRowHeight method for every row,
     * that's why we are calling it even when the density changes for the row.
     */
    const resetRowHeights = useCallback((onRowHeightReset = _.noop) => {
        onIdleRunner(cancelAllResetRowHeightIdle, () => {
            var _a;
            (_a = gridApi === null || gridApi === void 0 ? void 0 : gridApi.current) === null || _a === void 0 ? void 0 : _a.resetRowHeights();
            onRowHeightReset();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const tracedResetRowHeights = (trigger, onDone = _.noop) => {
        const t0 = Date.now();
        const span = startSpan(OtelOp.ROW_HEIGHT_RESET, otelContext, otelEnabled, { 'row_height.trigger': trigger });
        resetRowHeights(() => {
            span.setAttributes({ 'row_height.duration_ms': Date.now() - t0 });
            span.end();
            onDone();
        });
        if (!(gridDOMRef === null || gridDOMRef === void 0 ? void 0 : gridDOMRef.current) || !(gridApi === null || gridApi === void 0 ? void 0 : gridApi.current)) {
            span.end();
            onDone();
        }
    };
    useEffect(() => {
        return () => {
            var _a;
            // eslint-disable-next-line react-hooks/exhaustive-deps
            const timeoutIdsArray = timeoutIds.current;
            if (timeoutIdsArray.length) {
                timeoutIdsArray.forEach((id) => clearTimeout(id));
            }
            (_a = renderSpanRef.current) === null || _a === void 0 ? void 0 : _a.end();
            renderSpanRef.current = null;
        };
    }, []);
    // Row height for pinned row works exactly as for normal rows with one difference:
    // it is not possible to dynamically change the height once set.
    // However this is easily solved by just setting the pinned row data again which resets the row heights.
    // Setting the data again is not a problem for pinned rows as it doesn't impact scroll position, filtering,
    // sorting or group open / closed positions as it would with normal rows if the data was reset.
    // We need to change the summary height for present mode in Liveboards according to the screen width.
    useEffect(() => {
        var _a;
        // eslint-disable-next-line no-unused-expressions
        if (showSummary) {
            (_a = gridApi === null || gridApi === void 0 ? void 0 : gridApi.current) === null || _a === void 0 ? void 0 : _a.setPinnedBottomRowData([{}]);
        }
    }, [shouldScale, showSummary]);
    const getSummaryHeight = () => {
        var _a;
        const responsiveBreakpoint = (_a = getResponsiveBreakpoint()) === null || _a === void 0 ? void 0 : _a.screenSizeClass;
        if (responsiveBreakpoint !== undefined) {
            return screenSizeToSummaryHeightMapping[responsiveBreakpoint];
        }
        return undefined;
    };
    useEffect(() => {
        const updatedColumnHighLightAttr = _.transform(columns, (colHighlight, col) => {
            _.set(colHighlight, col.id, col.highlight || false);
        }, {});
        updateColumnHighlight(updatedColumnHighLightAttr);
    }, [columns]);
    const copyToClipboard = () => {
        const selectionData = getDataFromSelectionRange(selectedRange.current, gridApi, columns, context);
        copyTextToClipboard(selectionData);
    };
    const columnDefs = getColumnDefs(columns, {
        columnHighLightAttr,
        headerComponent,
        headerComponentParams,
        showSummary,
        summaryComponent,
        summaryComponentParams,
        cellLinkTarget,
        selectedRange: selectedRange.current,
        isHtmlRenderingDisabled,
    });
    const resizeDebounceRef = useRef(0);
    const resize = () => {
        if (resizeDebounceRef.current !== 0) {
            window.clearTimeout(resizeDebounceRef.current);
            resizeDebounceRef.current = 0;
        }
        resizeDebounceRef.current = setTimeout(() => {
            tracedResetRowHeights('resize');
            resetHeaderHeight(gridApi, gridDOMRef);
        }, 100);
    };
    const cancelAllRedrawIdle = () => {
        if (redrawIdleRef.current !== 0) {
            window.cancelIdleCallback(redrawIdleRef.current);
            redrawIdleRef.current = 0;
        }
    };
    const redrawRenderedRows = () => {
        var _a, _b;
        const rowNodes = (_a = gridApi.current) === null || _a === void 0 ? void 0 : _a.getRenderedNodes();
        (_b = gridApi === null || gridApi === void 0 ? void 0 : gridApi.current) === null || _b === void 0 ? void 0 : _b.redrawRows({ rowNodes });
    };
    const redraw = () => {
        onIdleRunner(cancelAllRedrawIdle, redrawRenderedRows);
    };
    useImperativeHandle(ref, () => ({
        copyToClipboard,
        resize,
        redraw,
    }));
    useEffect(() => {
        tracedResetRowHeights('column_change');
        /*
         * Added timeout because after wrap/clip operation (after element gets updated),
         * we need to pick DOM element to calculate the header height
         */
        resetHeaderHeight(gridApi, gridDOMRef);
    }, [columns, resetRowHeights]);
    const onDragStopped = React.useCallback((evt) => {
        const { columnApi } = evt;
        const colIds = columnApi
            .getAllDisplayedColumns()
            .map((col) => col.getColId());
        const columnOrder = _.map(columnDefs, 'field');
        if (!_.isEqual(colIds, columnOrder) && onColumnOrderUpdate) {
            onColumnOrderUpdate(_.compact(colIds));
        }
    }, [onColumnOrderUpdate, columnDefs]);
    const onColumnResized = ({ column, finished, source, }) => {
        const columnId = _.get(column, 'colDef.field');
        const newWidth = _.result(column, 'getActualWidth');
        if (onColumnResize && finished && source === 'uiColumnDragged') {
            onColumnResize(columnId, newWidth);
            tracedResetRowHeights('column_resize');
        }
        resetHeaderHeight(gridApi, gridDOMRef);
    };
    const getSelectedRowsData = (updatedSelectedRange = selectedRange.current) => {
        var _a;
        const selectedRowsData = [];
        const { startRowIndex, endRowIndex } = updatedSelectedRange;
        const minRowIndex = Math.min(startRowIndex, endRowIndex);
        const maxRowIndex = Math.max(startRowIndex, endRowIndex);
        for (let i = minRowIndex; i <= maxRowIndex; i += 1) {
            const rowNode = (_a = gridApi.current) === null || _a === void 0 ? void 0 : _a.getRowNode(`${i}`);
            selectedRowsData.push(rowNode === null || rowNode === void 0 ? void 0 : rowNode.data);
        }
        return selectedRowsData;
    };
    const getSelectedColumns = (updatedSelectedRange = selectedRange.current) => {
        let selectedColumnsRange = [];
        const { startColumnIndex, endColumnIndex } = updatedSelectedRange;
        const minColumnIndex = Math.min(startColumnIndex, endColumnIndex);
        const maxColumnIndex = Math.max(startColumnIndex, endColumnIndex);
        const selectedColumns = columns.slice(minColumnIndex, maxColumnIndex + 1);
        selectedColumnsRange = selectedColumns.map((col) => col.id);
        return selectedColumnsRange;
    };
    const onTableCellContextClicked = (eventParams) => {
        if (eventParams.node.rowPinned) {
            return;
        }
        const rowIndex = eventParams.rowIndex;
        const colIndex = eventParams.columnApi
            .getAllDisplayedColumns()
            .indexOf(eventParams.column);
        const updatedSelectedRange = getUpdatedSelectedRange(rowIndex || 0, colIndex, selectedRange.current);
        selectedRange.current = updatedSelectedRange;
        const colDef = eventParams.column.getUserProvidedColDef();
        const columnId = colDef === null || colDef === void 0 ? void 0 : colDef.field;
        const selectedColumns = getSelectedColumns(updatedSelectedRange);
        const selectedRowsData = getSelectedRowsData(updatedSelectedRange);
        onCellContextClicked(eventParams.event, {
            columnId,
            rowData: eventParams.data,
            selectedColumns,
            selectedRowsData,
        });
        forceRerenderForSelectedRange({});
    };
    const onTableCellClickedOrDoubleClicked = (callback) => (eventParams) => {
        if (eventParams.node.rowPinned) {
            return;
        }
        const colDef = eventParams.column.getUserProvidedColDef();
        const columnId = colDef === null || colDef === void 0 ? void 0 : colDef.field;
        const selectedColumns = getSelectedColumns();
        const selectedRowsData = getSelectedRowsData();
        callback(eventParams.event, {
            columnId,
            rowData: eventParams.data,
            selectedColumns,
            selectedRowsData,
            forceRerenderForSelectedRange,
        });
    };
    const onShiftKeyCellSelection = (keyEvent) => {
        const keyCodeArrowLeft = 37;
        const keyCodeArrowUp = 38;
        const keyCodeArrowRight = 39;
        const keyCodeArrowDown = 40;
        if (keyEvent.shiftKey) {
            switch (keyEvent.which) {
                case keyCodeArrowLeft:
                    selectedRange.current = {
                        ...selectedRange.current,
                        endColumnIndex: selectedRange.current.endColumnIndex - 1,
                    };
                    forceRerenderForSelectedRange({});
                    break;
                case keyCodeArrowUp:
                    selectedRange.current = {
                        ...selectedRange.current,
                        endRowIndex: selectedRange.current.endRowIndex - 1,
                    };
                    forceRerenderForSelectedRange({});
                    break;
                case keyCodeArrowRight:
                    selectedRange.current = {
                        ...selectedRange.current,
                        endColumnIndex: selectedRange.current.endColumnIndex + 1,
                    };
                    forceRerenderForSelectedRange({});
                    break;
                case keyCodeArrowDown:
                    selectedRange.current = {
                        ...selectedRange.current,
                        endRowIndex: selectedRange.current.endRowIndex + 1,
                    };
                    forceRerenderForSelectedRange({});
                    break;
                default:
            }
        }
    };
    const onCellMouseDownEvent = (e) => {
        var _a;
        const { event } = e;
        if (e.node.rowPinned ||
            /* below check is to prevent mouse down event whenever clicking on anchor link because
               mousedown event somehow blocking the click event(not on tap to click) on anchor tag
               which blocks the link to redirect. Required only when there is a caption link in a column
            */
            ((_a = event === null || event === void 0 ? void 0 : event.target) === null || _a === void 0 ? void 0 : _a.nodeName) === 'A' ||
            // You can tell its a doubleclick thats happened on the mousedown event if event.detail=2
            (event === null || event === void 0 ? void 0 : event.detail) === 2) {
            return;
        }
        // Preventing selection of normal cells during selection of footer / header
        const singleCellSelected = selectedRange.current.startColumnIndex ===
            selectedRange.current.endColumnIndex &&
            selectedRange.current.startRowIndex ===
                selectedRange.current.endRowIndex;
        const selectedRowIndex = e.rowIndex || 0;
        const selectedColIndex = e.columnApi
            .getAllDisplayedColumns()
            .indexOf(e.column);
        // when context menu is enabled on left click
        // we don't want to deselect the range selection
        // when we click on any of the selected cell
        if (isContextMenuEnabledOnLeftClick &&
            selectedRowIndex >= selectedRange.current.startRowIndex &&
            selectedRowIndex <= selectedRange.current.endRowIndex &&
            selectedColIndex >= selectedRange.current.startColumnIndex &&
            selectedColIndex <= selectedRange.current.endColumnIndex) {
            return;
        }
        if ((event === null || event === void 0 ? void 0 : event.buttons) === 1 || singleCellSelected) {
            const upDatedSelectedRange = {};
            upDatedSelectedRange.startRowIndex = selectedRowIndex;
            upDatedSelectedRange.endRowIndex = selectedRowIndex;
            upDatedSelectedRange.startColumnIndex = selectedColIndex;
            upDatedSelectedRange.endColumnIndex = selectedColIndex;
            selectedRange.current = upDatedSelectedRange;
        }
    };
    const onCellMouseOver = (e) => {
        var _a;
        const { event } = e;
        if ((event === null || event === void 0 ? void 0 : event.buttons) === 1) {
            const upatedSelectedRange = { ...selectedRange.current };
            upatedSelectedRange.endRowIndex = e.rowIndex || 0;
            upatedSelectedRange.endColumnIndex = (_a = e.columnApi
                .getAllDisplayedColumns()) === null || _a === void 0 ? void 0 : _a.indexOf(e.column);
            selectedRange.current = upatedSelectedRange;
            forceRerenderForSelectedRange({});
        }
    };
    const onCellKeyDown = (e) => {
        const { event } = e;
        const keyCodeC = 67;
        if (event.which === keyCodeC &&
            (event.ctrlKey || event.metaKey)) {
            copyToClipboard();
        }
        onShiftKeyCellSelection(event);
    };
    const onGridReady = (params) => {
        var _a, _b;
        gridApi.current = params.api;
        if (otelEnabled && otelContext) {
            renderT0Ref.current = Date.now();
            renderSpanRef.current = startSpan(OtelOp.RENDER, otelContext, otelEnabled, {
                'render.row_count': (_a = data === null || data === void 0 ? void 0 : data.length) !== null && _a !== void 0 ? _a : 0,
                'render.col_count': (_b = columns === null || columns === void 0 ? void 0 : columns.length) !== null && _b !== void 0 ? _b : 0,
            });
        }
    };
    const rowData = React.useMemo(() => hasMoreData
        ? [...data, [{ tsInternalShowloader: true }]]
        : data, [data, hasMoreData]);
    const isFullWidthCell = (rowNode) => {
        if (_.get(rowNode.data, '0.tsInternalShowloader')) {
            return true;
        }
        return false;
    };
    const onFirstDataRenderedCallback = (e) => {
        var _a, _b, _c, _d;
        // Initial header height rendering flow
        resetHeaderHeight(gridApi, gridDOMRef);
        // resetRowHeights callback below has a timeout due to which the onFirstDataRendered is getting called before the
        // row heights reset is completed. Therefore, we are passing onRowHeightReset to resetRowHeights
        // ensuring that onFirstDataRendered is called after the row heights are reset.
        const onRowHeightReset = () => {
            // After resetHeaderHeight and resetRowHeights, onFirstDataRendered should be called so that Ag Grid has
            // constant data to decide the scroll, especially for pinned rows.
            if (!_.isUndefined(onFirstDataRendered)) {
                onFirstDataRendered(e);
            }
        };
        // SCAL-101389
        // Ag-Grid has an issue in which the RowHeight of dynamic rows is inconsistent.
        // Ag-Grid constructs a dummy node, renders the value from cellRendererFramework in the same dummy node,
        // attaches it to the container 'ag-center-cols-container' (eWrapper), calculates the offset height,
        // and finally removes it from the container. Sometimes, due to early render cycles in Ag-Grid, the
        // value from cellRendererFramework does not get attached to the dummy node, and we see a default height of
        // 32px. Therefore, to ensure RowHeights are always consistent, we call `resetRowHeights` in
        // onFirstDataRenderedCallback.
        tracedResetRowHeights('first_render', onRowHeightReset);
        if (otelEnabled && otelContext) {
            (_a = renderSpanRef.current) === null || _a === void 0 ? void 0 : _a.setAttributes({
                'render.row_count': (_b = data === null || data === void 0 ? void 0 : data.length) !== null && _b !== void 0 ? _b : 0,
                'render.col_count': (_c = columns === null || columns === void 0 ? void 0 : columns.length) !== null && _c !== void 0 ? _c : 0,
                'render.duration_ms': Date.now() - renderT0Ref.current,
            });
            (_d = renderSpanRef.current) === null || _d === void 0 ? void 0 : _d.end();
            renderSpanRef.current = null;
        }
    };
    // TODO: to get px value with consideration of rems.
    // AgGrid is not considering and prop value changes while for e.g resizing
    // Therefore, putting this static function here.
    /*
        We are maintaining the row-cache to have row-index to row-height mapping,
        so that when in row-virtualisation row node got detach from the DOM and got added later due to scroll event,
        we have its height inplace to avoid the re-calculation of row-height
    */
    const rowCache = {};
    let $tableSelector = (_a = gridDOMRef === null || gridDOMRef === void 0 ? void 0 : gridDOMRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(ROW_CONTAINER_SELECTOR);
    const getRowHeight = (params) => {
        var _a, _b, _c, _d, _e;
        if (params) {
            if (_.get((_a = params === null || params === void 0 ? void 0 : params.node) === null || _a === void 0 ? void 0 : _a.data, '0.tsInternalShowloader')) {
                return DEFAULT_ROW_HEIGHT * 2;
            }
            if ((_b = params === null || params === void 0 ? void 0 : params.node) === null || _b === void 0 ? void 0 : _b.rowPinned) {
                if (shouldScale)
                    return getSummaryHeight();
                return DEFAULT_ROW_HEIGHT * 2;
            }
            if (!($tableSelector === null || $tableSelector === void 0 ? void 0 : $tableSelector.length)) {
                $tableSelector = (_c = gridDOMRef === null || gridDOMRef === void 0 ? void 0 : gridDOMRef.current) === null || _c === void 0 ? void 0 : _c.querySelectorAll(ROW_CONTAINER_SELECTOR);
            }
            if (params.node.rowIndex !== null) {
                // returning row height based on cell content (same as auto height)
                if (rowCache[(_d = params === null || params === void 0 ? void 0 : params.node) === null || _d === void 0 ? void 0 : _d.rowIndex]) {
                    return rowCache[(_e = params === null || params === void 0 ? void 0 : params.node) === null || _e === void 0 ? void 0 : _e.rowIndex];
                }
                if ($tableSelector === null || $tableSelector === void 0 ? void 0 : $tableSelector.length) {
                    const rowHeight = getHighestHeightOfRowChildren(params, $tableSelector, density, (height) => {
                        var _a;
                        if (params.node.rowIndex) {
                            rowCache[(_a = params === null || params === void 0 ? void 0 : params.node) === null || _a === void 0 ? void 0 : _a.rowIndex] = height;
                        }
                    });
                    return rowHeight;
                }
            }
        }
        return DEFAULT_ROW_HEIGHT;
    };
    const calculateRowHeight = () => getRowHeight();
    /*
        we are only calculating the height of ag-grid row when present in DOM,
        while scrolling few other elements got added into the DOM,
        that's why we need to call resetRowHeights() which eventually will call getRowHeight()
        to calculate the row-height. Below method will get called only when user stops scrolling.
        We are doing this manual calculation of row-height to mimic the behaviour of autoHeight feature in ag-grid
    */
    const onBodyScrollEnd = (event) => {
        if (event.direction === 'vertical') {
            const timeoutId = setTimeout(() => {
                var _a;
                $tableSelector = (_a = gridDOMRef === null || gridDOMRef === void 0 ? void 0 : gridDOMRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(ROW_CONTAINER_SELECTOR);
                tracedResetRowHeights('scroll_end');
            }, 300);
            timeoutIds.current.push(timeoutId);
        }
    };
    const tableContext = React.useMemo(() => {
        return {
            ...context,
            density,
            selectedRange: { ...selectedRange },
        };
    }, [context, density, selectedRange]);
    const fullWidthCellRendererParams = React.useMemo(() => {
        return {
            onLoad: (contextParams) => {
                if (_.isUndefined(onBodyScrollToBottom)) {
                    return;
                }
                window.requestIdleCallback(() => onBodyScrollToBottom(contextParams));
            },
        };
    }, [onBodyScrollToBottom]);
    const onRowDataChanged = (event) => {
        var _a, _b;
        if (otelEnabled && otelContext) {
            const t0 = Date.now();
            const span = startSpan(OtelOp.DATA_UPDATE, otelContext, otelEnabled, {
                'data.row_count': (_a = data === null || data === void 0 ? void 0 : data.length) !== null && _a !== void 0 ? _a : 0,
                'data.col_count': (_b = columns === null || columns === void 0 ? void 0 : columns.length) !== null && _b !== void 0 ? _b : 0,
            });
            onRowDataChangedCb === null || onRowDataChangedCb === void 0 ? void 0 : onRowDataChangedCb(event);
            tracedResetRowHeights('data_update', () => {
                span.setAttributes({ 'data.duration_ms': Date.now() - t0 });
                span.end();
            });
        }
        else {
            onRowDataChangedCb === null || onRowDataChangedCb === void 0 ? void 0 : onRowDataChangedCb(event);
            tracedResetRowHeights('data_update');
        }
    };
    let onCellContextMenuHandler = onTableCellContextClicked;
    let onCellClickedHandler = onTableCellClickedOrDoubleClicked(onCellClicked);
    if (isContextMenuEnabledOnLeftClick) {
        onCellContextMenuHandler = onTableCellClickedOrDoubleClicked(onCellClicked);
        onCellClickedHandler = onTableCellContextClicked;
    }
    if (isContextMenuEnabledOnLeftClick &&
        isContextMenuEnabledOnRightClick) {
        onCellContextMenuHandler = onTableCellContextClicked;
        onCellClickedHandler = onTableCellContextClicked;
    }
    const getRowStyle = (params) => {
        var _a, _b;
        if (!calcRowStyle) {
            return undefined;
        }
        const dataModel = (_a = params === null || params === void 0 ? void 0 : params.context) === null || _a === void 0 ? void 0 : _a.modelState;
        const rowData = params.data;
        return (_b = calcRowStyle(dataModel, rowData)) === null || _b === void 0 ? void 0 : _b.rowStyle;
    };
    const getRowClass = (params) => {
        var _a, _b, _c;
        if (!calcRowStyle) {
            return [];
        }
        const dataModel = (_a = params === null || params === void 0 ? void 0 : params.context) === null || _a === void 0 ? void 0 : _a.modelState;
        const rowData = params.data;
        return (_c = (_b = calcRowStyle(dataModel, rowData)) === null || _b === void 0 ? void 0 : _b.rowClass) !== null && _c !== void 0 ? _c : [];
    };
    const onSortChanged = (e) => {
        const sortStateModel = e.columnApi
            .getColumnState()
            .find(({ sort }) => !!sort);
        if (onSortingChanged && sortStateModel)
            onSortingChanged([sortStateModel]);
    };
    // Dark mode is driven purely by the [data-theme='dark'] attribute on
    // <html>; the wrapping div always carries `ag-theme-alpine`. See
    // table-override.scss for the cascade-based light/dark theming.
    return (React.createElement(WrapAsCordComponent, { location: location, className: cx(THEME_TO_CLASSNAMES[theme], styles.fullContainer), cordAnnotationCaptureHandler: cordAnnotationCaptureHandler, cordAnnotationRendererHandler: cordAnnotationRenderer, cordAnnotationClickHandler: cordAnnotationClickHandler },
        React.createElement("div", { className: cx(THEME_TO_CLASSNAMES[theme], styles.fullContainer) },
            React.createElement("div", { className: cx('ag-theme-alpine', styles.fullContainer), ref: gridDOMRef },
                React.createElement(AgGridReact, { rowData: rowData, columnDefs: columnDefs, rowHeight: DEFAULT_ROW_HEIGHT, getRowHeight: getRowHeight, getRowStyle: getRowStyle, getRowClass: getRowClass, suppressDragLeaveHidesColumns: true, onColumnResized: _.throttle(onColumnResized, 200), onDragStopped: onDragStopped, defaultColDef: DEFAULT_COL_DEF, suppressHorizontalScroll: false, scrollbarWidth: 8, onGridReady: onGridReady, modules: [ClientSideRowModelModule], onCellContextMenu: onCellContextMenuHandler, onCellDoubleClicked: onTableCellClickedOrDoubleClicked(onCellDoubleClicked), onCellClicked: onCellClickedHandler, pinnedBottomRowData: showSummary ? [{}] : undefined, preventDefaultOnContextMenu: true, debounceVerticalScrollbar: isDebounceVerticalScrollbar, onRowDataChanged: onRowDataChanged, onCellMouseDown: onCellMouseDownEvent, onCellMouseOver: _.throttle(onCellMouseOver, 200), onCellKeyDown: onCellKeyDown, onFirstDataRendered: onFirstDataRenderedCallback, context: tableContext, 
                    //  applyColumnDefOrder, its now default behaviour in v27
                    suppressScrollOnNewData: true, isFullWidthCell: isFullWidthCell, fullWidthCellRenderer: LoaderComponent, enableCellChangeFlash: false, fullWidthCellRendererParams: fullWidthCellRendererParams, rowBuffer: isPrintMode ? 999 : 25, suppressColumnVirtualisation: isPrintMode
                        ? true
                        : suppressColumnVirtualisation, suppressMaxRenderedRowRestriction: isPrintMode, 
                    // stop default row highlight on hover
                    suppressRowHoverHighlight: true, onPinnedRowDataChanged: calculateRowHeight, onBodyScrollEnd: onBodyScrollEnd, onSortChanged: onSortChanged })))));
});
export const Table = ({ location, data, columns, onColumnOrderUpdate, onColumnResize, isPrintMode = false, calcRowStyle, headerComponent, headerComponentParams, className = '', refToForward, showSummary = false, summaryComponent, summaryComponentParams, theme, onCellContextClicked, getAnnotationLocationConfig, getAnnotationRenderedConfig, annotationClickEventHandler, onCellDoubleClicked = _.noop, onCellClicked = _.noop, density, onRowDataChangedCb = _.noop, onFirstDataRendered = _.noop, context, onBodyScrollToBottom, hasMoreData, cellLinkTarget, suppressColumnVirtualisation = false, isDebounceVerticalScrollbar = true, shouldScale = false, isContextMenuEnabledOnLeftClick = false, isContextMenuEnabledOnRightClick = true, isHtmlRenderingDisabled = false, onSortingChanged, themeMode, otelContext, otelEnabled, ...restProps }) => {
    // todo: SCAL-60442 Render no data component.
    const innerView = (React.createElement(TableDataView, { location: location, data: data, columns: columns, onColumnOrderUpdate: onColumnOrderUpdate, onColumnResize: onColumnResize, isPrintMode: isPrintMode, ref: refToForward, calcRowStyle: calcRowStyle, getAnnotationLocationConfig: getAnnotationLocationConfig, getAnnotationRenderedConfig: getAnnotationRenderedConfig, showSummary: showSummary, annotationClickEventHandler: annotationClickEventHandler, summaryComponent: summaryComponent, summaryComponentParams: summaryComponentParams, headerComponent: headerComponent, headerComponentParams: headerComponentParams, theme: theme, themeMode: themeMode, density: density, onFirstDataRendered: onFirstDataRendered, onCellContextClicked: onCellContextClicked, onCellDoubleClicked: onCellDoubleClicked, onCellClicked: onCellClicked, onRowDataChangedCb: onRowDataChangedCb, context: context, onBodyScrollToBottom: onBodyScrollToBottom, hasMoreData: hasMoreData, cellLinkTarget: cellLinkTarget, suppressColumnVirtualisation: suppressColumnVirtualisation, isDebounceVerticalScrollbar: isDebounceVerticalScrollbar, shouldScale: shouldScale, isContextMenuEnabledOnLeftClick: isContextMenuEnabledOnLeftClick, isContextMenuEnabledOnRightClick: isContextMenuEnabledOnRightClick, isHtmlRenderingDisabled: isHtmlRenderingDisabled, onSortingChanged: onSortingChanged, otelContext: otelContext, otelEnabled: otelEnabled }));
    return (React.createElement("div", { className: cx(styles.fullContainer, className), ...restProps }, innerView));
};
//# sourceMappingURL=table.js.map